/**
 * The video popup in the article editor: the City's player for a meeting, the transcript scrolling with it, and from
 * any moment: a quote (the speaker's exact words, attributed for you), a clip (set its start and end while watching; opened
 * from a clip in the text, "Update clip" changes that clip), a screen grab (a frame of the video at that second, uploaded
 * as an article image), or the article's image.
 *
 * Screen grabs: the Worker returns the piece of the HLS video around the moment as a small MP4
 * (/api/dashboard/articles/frame); a <video> here seeks inside it and the frame is drawn to a canvas.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import Player from "@vimeo/player";
import { Camera, Quote, Scissors, X } from "lucide-react";

const API = "/api/dashboard/articles";
interface Turn { speakerId: string; name: string; ward: number | null; role: string | null; quotable: boolean; start: number; end: number; text: string }
interface Passages { meetingId: string; body: string | null; date: string | null; vimeo: string | null; turns: Turn[] }

export const clock = (sec: number) => { const s = Math.max(0, Math.floor(sec)), h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60; return h ? `${h}:${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}` : `${m}:${String(r).padStart(2, "0")}`; };
const ordinal = (n: number) => `${n}${[11, 12, 13].includes(n % 100) ? "th" : ["th", "st", "nd", "rd"][n % 10] || "th"}`;
const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("en-US", { timeZone: "America/Chicago", month: "short", day: "numeric", year: "numeric" }) : "");
/** "Ald. Raymond A. Lopez (15th Ward)" for an alderperson; the name otherwise. */
export const who = (t: Pick<Turn, "name" | "ward">) => (t.ward ? `Ald. ${t.name} (${ordinal(t.ward)} Ward)` : t.name);

export interface VideoPick {
  meetingId: string; vimeo: string; start: number;
  label: string;   // "City Council, Sept. 23, 2026"
  /** Opened from a clip in the text: its times and caption are loaded, and "Update clip" changes it in place. */
  existing?: { start: string; end: string; caption: string; apply: (c: { start: string; end: string; caption: string }) => void };
}
interface Props {
  pick: VideoPick; onClose: () => void;
  onQuote: (md: string) => void; onClip: (md: string) => void; onImage: (md: string) => void; onHero: (url: string, alt: string) => void;
  upload: (file: Blob, name?: string) => Promise<string | null>;
}
const seconds = (s: string) => s.split(":").reduce((a, x) => a * 60 + Number(x), 0);

export function VideoModal({ pick, onClose, onQuote, onClip, onImage, onHero, upload }: Props) {
  const frame = useRef<HTMLIFrameElement>(null);
  const player = useRef<Player | null>(null);
  const [t, setT] = useState(pick.start);
  const [passages, setPassages] = useState<Passages | null>(null);
  const [clip, setClip] = useState<{ start: number | null; end: number | null; caption: string }>(pick.existing
    ? { start: seconds(pick.existing.start), end: pick.existing.end ? seconds(pick.existing.end) : null, caption: pick.existing.caption }
    : { start: null, end: null, caption: "" });
  const [busy, setBusy] = useState<string | null>(null);
  const [grab, setGrab] = useState<{ url: string; alt: string; caption: string } | null>(null);
  const bucket = Math.floor(t / 20);

  useEffect(() => {
    if (!frame.current) return;
    const p = new Player(frame.current);
    player.current = p;
    p.on("timeupdate", (e: { seconds: number }) => setT(e.seconds));
    return () => { p.unload().catch(() => {}); player.current = null; };
  }, [pick.vimeo]);
  useEffect(() => {
    let on = true;
    fetch(`${API}/passages?meeting=${encodeURIComponent(pick.meetingId)}&t=${Math.floor(t)}&window=45`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null)).then((d) => { if (on && d) setPassages(d); }).catch(() => {});
    return () => { on = false; };
  }, [bucket, pick.meetingId]); // eslint-disable-line react-hooks/exhaustive-deps

  const current = useMemo(() => passages?.turns.find((x) => x.start <= t && t <= x.end + 2) ?? null, [passages, t]);
  const where = `${passages?.body ?? pick.label.split(",")[0]}, ${passages?.date ? day(passages.date) : pick.label.split(", ").slice(1).join(", ")}`;
  const seek = (s: number) => player.current?.setCurrentTime(s).catch(() => {});

  const quote = (turn: Turn) => {
    const sel = window.getSelection()?.toString().trim();
    const inTurn = sel && turn.text.includes(sel) ? sel : null;
    let text = inTurn ?? turn.text;
    if (!inTurn && text.split(/\s+/).length > 70) text = text.split(/\s+/).slice(0, 70).join(" ") + "…";
    onQuote(`> "${text.replace(/"/g, "'")}"\n> — ${who(turn)}, ${where}`);
  };
  const insertClip = () => {
    if (clip.start == null) return;
    const end = clip.end != null && clip.end > clip.start ? clip.end : Math.min(clip.start + 60, clip.start + 120);
    const caption = clip.caption || (current ? `${who(current)} on the floor` : "");
    if (pick.existing) { pick.existing.apply({ start: clock(clip.start), end: clock(end), caption }); onClose(); return; }
    onClip(`[[clip ${pick.meetingId} ${clock(clip.start)}-${clock(end)}${caption ? ` "${caption.replace(/"/g, "'")}"` : ""}]]`);
  };
  const screenGrab = async () => {
    setBusy("grab");
    try {
      const at = Math.floor(t);
      const r = await fetch(`${API}/frame?vimeo=${encodeURIComponent(pick.vimeo)}&t=${at}`, { credentials: "include" });
      if (!r.ok) throw new Error(await r.text());
      const segStart = Number(r.headers.get("x-segment-start") || 0);
      const blobUrl = URL.createObjectURL(await r.blob());
      const v = document.createElement("video");
      v.muted = true; v.preload = "auto"; v.src = blobUrl;
      await new Promise<void>((ok, no) => { v.onloadedmetadata = () => ok(); v.onerror = () => no(new Error("The video piece couldn't be decoded.")); });
      v.currentTime = Math.max(0, Math.min(v.duration - 0.05, at - segStart + 0.4));
      await new Promise<void>((ok, no) => { v.onseeked = () => ok(); v.onerror = () => no(new Error("Couldn't seek.")); });
      const c = document.createElement("canvas");
      c.width = v.videoWidth; c.height = v.videoHeight;
      c.getContext("2d")!.drawImage(v, 0, 0);
      URL.revokeObjectURL(blobUrl);
      const jpg = await new Promise<Blob | null>((ok) => c.toBlob(ok, "image/jpeg", 0.86));
      if (!jpg) throw new Error("Couldn't draw the frame.");
      const url = await upload(jpg, `frame-${pick.vimeo}-${at}.jpg`);
      if (!url) throw new Error("Upload failed.");
      const alt = current ? `${who(current)} at the ${where}` : `The ${where}`;
      setGrab({ url, alt, caption: `${alt}. City of Chicago video.` });
    } catch (e) {
      alert(String((e as Error).message || e));
    }
    setBusy(null);
  };

  return (
    <div className="omb-video" role="dialog" aria-modal="true" aria-label={`Video: ${pick.label}`} onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}>
      <div className="omb-video__panel">
        <div className="omb-video__head">
          <b>{pick.label}</b><span className="muted art-small"> · {clock(t)}</span>
          <button type="button" className="link-button omb-video__close" onClick={onClose} aria-label="Close"><X size={18} aria-hidden /></button>
        </div>
        <div className="omb-video__body">
          <div className="omb-video__player">
            <iframe ref={frame} src={`https://player.vimeo.com/video/${pick.vimeo}?dnt=1#t=${Math.floor(pick.start)}s`} title={pick.label} allow="autoplay; fullscreen; picture-in-picture" allowFullScreen />
            <div className="omb-video__tools">
              <button type="button" className="btn btn--secondary btn--small" onClick={() => setClip({ ...clip, start: Math.floor(t) })}><Scissors size={14} aria-hidden />Clip starts here{clip.start != null ? ` (${clock(clip.start)})` : ""}</button>
              <button type="button" className="btn btn--secondary btn--small" disabled={clip.start == null} onClick={() => setClip({ ...clip, end: Math.ceil(t) })}>Ends here{clip.end != null ? ` (${clock(clip.end)})` : ""}</button>
              <input className="text-input omb-video__caption" value={clip.caption} onChange={(e) => setClip({ ...clip, caption: e.target.value })} placeholder={current ? `${who(current)} on …` : "Clip caption"} aria-label="Clip caption" />
              <button type="button" className="btn btn--primary btn--small" disabled={clip.start == null} onClick={insertClip}>{pick.existing ? "Update clip" : "Add clip"}</button>
              <button type="button" className="btn btn--secondary btn--small" disabled={busy === "grab"} onClick={screenGrab}><Camera size={14} aria-hidden />{busy === "grab" ? "Grabbing…" : "Screen grab"}</button>
            </div>
            {grab && (
              <div className="omb-video__grab">
                <img src={grab.url} alt={grab.alt} />
                <input className="text-input" value={grab.caption} onChange={(e) => setGrab({ ...grab, caption: e.target.value })} aria-label="Caption" />
                <span className="art-row-actions">
                  <button type="button" className="btn btn--primary btn--small" onClick={() => { onImage(`![${grab.alt}](${grab.url} "${grab.caption.replace(/"/g, "'")}")`); setGrab(null); }}>Add to article</button>
                  <button type="button" className="btn btn--secondary btn--small" onClick={() => { onHero(grab.url, grab.alt); setGrab(null); }}>Use as the article's image</button>
                  <button type="button" className="link-button" onClick={() => setGrab(null)}>Discard</button>
                </span>
              </div>
            )}
          </div>
          <div className="omb-video__transcript" aria-live="polite">
            {!passages ? <p className="muted">Loading the transcript…</p> : !passages.turns.length ? <p className="muted">Nothing transcribed around here.</p> : passages.turns.map((x) => (
              <div key={`${x.speakerId}-${x.start}`} className={`omb-video__turn${x === current ? " is-now" : ""}${x.quotable ? "" : " is-private"}`}>
                <p className="omb-video__who"><button type="button" className="link-button" onClick={() => seek(x.start)}>{clock(x.start)}</button> <b>{x.quotable ? who(x) : "Public comment"}</b>{x.role && x.quotable ? <span className="muted"> · {x.role}</span> : null}</p>
                <p className="omb-video__text">{x.text}</p>
                {x.quotable ? <button type="button" className="btn btn--secondary btn--small" onClick={() => quote(x)}><Quote size={14} aria-hidden />Quote{window.getSelection()?.toString().trim() ? " selection" : ""}</button>
                  : <p className="muted art-small">Residents speaking in public comment aren't named or quoted.</p>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
